import { useRecoilValue } from 'recoil';
import { useMemo } from 'react';
import { Dayjs } from 'dayjs';
import {
  dataTrendPeriodAtom,
  getWeekDatesOf,
  PeakDipPhase,
  useAuthContext,
  useTaskDailyTrend,
  useTaskWeeklyTrend,
} from '../data-access';
import { getCssVariable } from '../utils';
import { WeekBarChartItem, zoneColorForPeaksDipsPhase } from '../components';

interface UseTaskTrendParams {
  date: Dayjs;
}

const primaryColor = getCssVariable('--color-primary') || '#680ddb';
const grayBase = getCssVariable('--color-gray-base') || '#6B7280';

export const useTaskTrend = ({ date }: UseTaskTrendParams) => {

  const dataTrendPeriod = useRecoilValue(dataTrendPeriodAtom);
  const { user } = useAuthContext();

  const { taskTrend: taskDailyTrend, isLoading: isDailyLoading } = useTaskDailyTrend({
    dataTrendId: user.dataTrendId!,
    date: date.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'daily',
  });

  const weekEdge = useMemo(() => {
    const week = getWeekDatesOf(date);
    return {
      startDate: week[0],
      endDate: week[week.length - 1],
    };
  }, [date]);

  const { taskTrend: taskWeeklyTrend, isLoading: isWeeklyLoading } = useTaskWeeklyTrend({
    dataTrendId: user.dataTrendId!,
    startDate: weekEdge.startDate.format('YYYY-MM-DD'),
    endDate: weekEdge.endDate.format('YYYY-MM-DD'),
    enabled: dataTrendPeriod === 'weekly',
  });

  const isLoading = dataTrendPeriod === 'daily' ? isDailyLoading : isWeeklyLoading;

  const commonData = useMemo(() => {
    if (dataTrendPeriod === 'daily') {
      return {
        taskCompletion: taskDailyTrend?.taskCompletionComparison,
        doneTaskComparison: taskDailyTrend?.doneTaskComparison,
        trendByLabels: taskDailyTrend?.trendByLabels ?? [],
        trendByZone: taskDailyTrend?.trendByZone ?? [],
      };
    } else {
      return {
        taskCompletion: taskWeeklyTrend?.taskCompletionComparison,
        doneTaskComparison: taskWeeklyTrend?.doneTaskComparison,
        trendByLabels: taskWeeklyTrend?.trendByLabels ?? [],
        trendByZone: taskWeeklyTrend?.trendByZone ?? [],
      };
    }
  }, [taskDailyTrend, taskWeeklyTrend, dataTrendPeriod]);

  const reeWeeklyDoneTasksData = useMemo(() => {
    if (!taskWeeklyTrend?.completedTasksByDay || !taskWeeklyTrend?.trendByLabels) {
      return { data: [], keys: [], colors: [], labels: [] };
    }

    const labelNames = taskWeeklyTrend.trendByLabels.map((l) => l.labelName);
    const labelColors = taskWeeklyTrend.trendByLabels.map((l) => l.labelColor || primaryColor);

    const zoneNames = taskWeeklyTrend.trendByZone?.map((z) => z.zoneName) || [];

    const zoneColors = zoneNames.map(
      (zoneName) => zoneColorForPeaksDipsPhase[zoneName as PeakDipPhase] || grayBase
    );

    const weekDays = Object.keys(taskWeeklyTrend.completedTasksByDay);

    const data = weekDays.map((day) => {
      const tasks = taskWeeklyTrend.completedTasksByDay[day] || [];

      const labelCounts = labelNames.reduce(
        (acc, label) => {
          acc[label] = 0;
          return acc;
        },
        {} as Record<string, number>
      );

      const zoneCounts = zoneNames.reduce(
        (acc, zone) => {
          acc[zone] = 0;
          return acc;
        },
        {} as Record<string, number>
      );

      tasks.forEach((task) => {
        (task.labels || []).forEach((labelObj) => {
          if (labelCounts.hasOwnProperty(labelObj.name)) {
            labelCounts[labelObj.name] += 1;
          }
        });

        if (task.addedZonePhase && zoneCounts.hasOwnProperty(task.addedZonePhase)) {
          zoneCounts[task.addedZonePhase] += 1;
        }
      });

      return {
        day,
        ...labelCounts,
        ...zoneCounts,
      };
    });

    const dayLabels = weekDays.map((_, idx) => ['S', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'][idx]);

    return {
      zone: {
        data: data.map((dayData) => {
          const zoneData = {};
          zoneNames.forEach((zoneName) => {
            zoneData[zoneName] = dayData[zoneName];
          });
          return { day: dayData.day, ...zoneData };
        }),
        keys: zoneNames,
        colors: zoneColors,
        labels: dayLabels,
      },
      labels: {
        data: data.map((dayData) => {
          const labelData = {};
          labelNames.forEach((labelName) => {
            labelData[labelName] = dayData[labelName];
          });
          return { day: dayData.day, ...labelData };
        }),
        keys: labelNames,
        colors: labelColors,
        labels: dayLabels,
      },
    };
  }, [taskWeeklyTrend]);

  const weeklyDoneTasksData = useMemo(() => {
    return (
      reeWeeklyDoneTasksData || {
        zone: { data: [], keys: [], colors: [], labels: [] },
        labels: { data: [], keys: [], colors: [], labels: [] },
      }
    );
  }, [taskWeeklyTrend, reeWeeklyDoneTasksData]);

  return {
    dataTrendPeriod,
    commonData,
    weeklyDoneTasksData,
    taskDailyTrend,
    taskWeeklyTrend,
    isLoading,
  };
};
